import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Dialog from "src/components/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import { GridSelectionModel } from "@material-ui/x-grid";

import EnhacedXGrid from "src/components/EnhacedXGrid";

import { setActiveDialog } from "src/store/adminHtml/actions";
import { getUnreviewedSales } from "src/store/sale/selector";
import { reviewManySalesOptimistic } from "src/store/sale/actions";
import { formatCurrency } from "src/utils";
import { StatefulLoadableButton } from "src/components/LoadableButton";
import { userRoleSelector } from "src/store/system/selector";
import ReviewQueueStatus from "src/components/ReviewQueueStatus";
import { useReviewQueueLoader } from "src/utils/userData/useReviewQueueLoader";

// import SalesMatchingDialog from "src/pages/RootDialogs/SalesMatching";
import SalesMatchingDialog from "./SalesMatching";
import VendorField from "src/components/VendorField";
import WalkTrhough from "./WalkThrough";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "80vh",
    display: "relative",
  },
}));

const Columns = [
  {
    field: "item_title",
    headerName: "Item Title",
    flex: 1,
  },
  {
    field: "sku",
    headerName: "SKU",
    width: 200,
  },
  {
    field: "sale_date",
    headerName: "Sale Date",
    type: "date",
    width: 150,
  },
  {
    field: "sale_price",
    headerName: "Sale Price",
    width: 150,
    valueFormatter: formatCurrency,
  },
];

function SalesReviewContent({
  onMatchToInventoryClick,
  onClose,
  ready,
  queueLoading,
  queueError,
  onRetry,
}: {
  onMatchToInventoryClick: (sales: any[]) => void;
  onClose: () => void;
  ready: boolean;
  queueLoading: boolean;
  queueError: unknown;
  onRetry: () => void;
}) {
  const dispatch = useDispatch();
  const [vendors, setVendors] = useState<Record<string, string>>({});
  const classes = useStyles();
  const unreviewedSales = useSelector(getUnreviewedSales);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const userRole = useSelector(userRoleSelector);
  const handlersRef = useRef({
    vendors,
    setVendors,
  });

  handlersRef.current.vendors = vendors;
  handlersRef.current.setVendors = setVendors;
  const hadUnreviewedSalesRef = useRef(false);

  const columns = useMemo(() => {
    const [title, ...rest] = Columns;
    return [
      title,
      {
        field: "vendor",
        headerName: "Vendor",
        width: 200,
        renderCell: (params) => {
          return (
            <VendorField
              label={null}
              margin="dense"
              value={handlersRef.current.vendors[params.id]}
              onChange={(vendor) => {
                handlersRef.current.setVendors((v) => ({
                  ...v,
                  [params.id]: vendor,
                }));
              }}
            />
          );
        },
      },
      ...rest,
    ];
  }, []);

  // Close once all previously-unreviewed sales have been reviewed.
  useEffect(() => {
    if (queueLoading || queueError) return;
    if (unreviewedSales.length > 0) {
      hadUnreviewedSalesRef.current = true;
      return;
    }

    if (hadUnreviewedSalesRef.current) {
      onClose();
    }
  }, [queueLoading, queueError, unreviewedSales.length, onClose]);

  return (
    <>
      <DialogTitle>Review Sales</DialogTitle>
      <DialogContent className={classes.container}>
        {queueLoading || queueError ? (
          <ReviewQueueStatus
            loading={queueLoading}
            error={queueError}
            onRetry={onRetry}
            label="sales to review"
          />
        ) : (
          ready && (
            <EnhacedXGrid
              selectionModel={selectionModel}
              onSelectionModelChange={(sm) => setSelectionModel(sm)}
              checkboxSelection
              disableSelectionOnClick
              columns={columns}
              rows={unreviewedSales}
              className="review-sales-dialog"
            />
          )
        )}
      </DialogContent>
      <DialogActions>
        <div
          id="review-sales-walkthough-actions"
          className="MuiDialogActions-spacing"
        >
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              if (selectionModel?.length)
                onMatchToInventoryClick(
                  selectionModel.map((id) =>
                    unreviewedSales.find((s) => s.id === id)
                  )
                );
            }}
            disabled={
              unreviewedSales.length === 0 || selectionModel.length === 0
            }
          >
            Match to Inventory
          </Button>
          <StatefulLoadableButton
            color="primary"
            variant="contained"
            disabled={selectionModel.length === 0 || userRole === "viewer"}
            onClick={async () => {
              await dispatch(
                reviewManySalesOptimistic(
                  selectionModel.map((id) => `${id}`),
                  false,
                  vendors
                )
              );
              toast.success("Sales marked as reviewed.");
              setSelectionModel([]);
            }}
          >
            Mark Reviewed
          </StatefulLoadableButton>
        </div>
      </DialogActions>
    </>
  );
}

export interface SalesReviewProps {
  open: boolean;
}

function SalesReview({ open }: SalesReviewProps) {
  const dispatch = useDispatch();
  const queue = useReviewQueueLoader(open, "sales");
  const [unreviewedSales, setUnreviewedSales] = useState<any[] | undefined>(
    undefined
  );
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setUnreviewedSales(undefined);
      setReady(false);
    }
  }, [open]);
  const openMatching = !!unreviewedSales;
  return (
    <>
      <Dialog
        open={open && !openMatching}
        onClose={() => dispatch(setActiveDialog(""))}
        fullWidth={true}
        maxWidth="lg"
        TransitionProps={{
          onEntered: () => setReady(true),
        }}
      >
        <SalesReviewContent
          onMatchToInventoryClick={(sales) => {
            setUnreviewedSales(sales);
          }}
          onClose={() => dispatch(setActiveDialog(""))}
          ready={ready}
          queueLoading={queue.loading}
          queueError={queue.error}
          onRetry={queue.retry}
        />
        {!queue.loading && !queue.error && <WalkTrhough />}
      </Dialog>
      <SalesMatchingDialog
        open={open && openMatching}
        unreviewedSales={unreviewedSales}
      />
    </>
  );
}

export default SalesReview;
